import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  qDeliveryOrderLinesByOrder,
  qDeliveryOrdersList,
  type DeliveryOrderLineRow,
  type DeliveryOrderRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { useBrandTokens, type ShellOutletContext } from '../../shell/shared';
import {
  InventoryListToolbar,
  InventoryPageShell,
  InventoryPrimaryAction,
  InventoryRow,
  InventoryRowActions,
  InventoryStatusBadge,
  InventoryTable,
  type InventoryStatusVariant,
} from './_shared';
import { DeliveryOrderDetailsDrawer } from './DeliveryOrderDetailsDrawer';
import { PurchaseOrderDetailsDrawer } from './PurchaseOrderDetailsDrawer';
import { ReceivePoDrawer } from './ReceivePoDrawer';

const COLUMNS = [
  { label: 'DO Number', width: '160px' },
  { label: 'Received', width: '110px' },
  { label: 'Supplier', width: '1.2fr' },
  { label: 'Reference', width: '160px' },
  { label: 'Status', width: '120px' },
  { label: 'Lines', width: '90px', align: 'right' as const },
  { label: 'Total', width: '120px', align: 'right' as const },
  { label: 'Actions', width: '90px', align: 'right' as const },
];

function statusVariant(status: string | null): InventoryStatusVariant {
  if (status === 'cancelled') return 'cancelled';
  if (status === 'received') return 'received';
  return 'draft';
}

export function DeliveryOrdersPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const brandTokens = useBrandTokens();

  const { data: rows = [] } = usePowerSyncQuery<DeliveryOrderRow>(qDeliveryOrdersList());

  const [search, setSearch] = useState('');
  const [selectedDoId, setSelectedDoId] = useState<string | null>(null);
  const [viewPoId, setViewPoId] = useState<string | null>(null);
  const [receivePoDrawerOpen, setReceivePoDrawerOpen] = useState(false);
  const selectedDo = useMemo(
    () => (selectedDoId ? (rows.find((r) => r.id === selectedDoId) ?? null) : null),
    [rows, selectedDoId],
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        (r.do_number ?? '').toLowerCase().includes(q) ||
        (r.supplier_name ?? '').toLowerCase().includes(q) ||
        (r.supplier_invoice_number ?? '').toLowerCase().includes(q) ||
        (r.reference ?? '').toLowerCase().includes(q) ||
        (r.notes ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <InventoryPageShell
      title="Delivery Orders"
      subtitle="Receive and verify stock deliveries from suppliers — finalize creates inbound stock movements"
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <InventoryPrimaryAction
          label="Receive Purchase Order"
          onClick={() => setReceivePoDrawerOpen(true)}
        />
      }
    >
      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search DO number, supplier, invoice, reference..."
      />
      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />
      <InventoryTable
        columns={COLUMNS}
        empty={
          filtered.length === 0
            ? {
                show: true,
                title: 'No delivery orders yet.',
                hint: 'Open a Purchase Order and click Receive to record the supplier delivery.',
              }
            : undefined
        }
      >
        {filtered.map((row) => (
          <DoListRow key={row.id} row={row} onOpen={() => setSelectedDoId(row.id)} />
        ))}
      </InventoryTable>
      <DeliveryOrderDetailsDrawer
        open={selectedDo != null}
        onOpenChange={(o) => {
          if (!o) setSelectedDoId(null);
        }}
        deliveryOrder={selectedDo}
        brandTokens={brandTokens}
        onViewPo={(poId) => setViewPoId(poId)}
      />
      <PurchaseOrderDetailsDrawer
        open={viewPoId != null}
        onOpenChange={(o) => {
          if (!o) setViewPoId(null);
        }}
        purchaseOrderId={viewPoId}
        brandTokens={brandTokens}
      />
      <ReceivePoDrawer
        open={receivePoDrawerOpen}
        onOpenChange={setReceivePoDrawerOpen}
        brandTokens={brandTokens}
      />
    </InventoryPageShell>
  );
}

function DoListRow({ row, onOpen }: { row: DeliveryOrderRow; onOpen: () => void }) {
  const { data: lineRows = [] } = usePowerSyncQuery<DeliveryOrderLineRow>(
    qDeliveryOrderLinesByOrder(row.id),
  );
  const fmt = useFormatMoney();
  return (
    <InventoryRow columns={COLUMNS} onClick={onOpen} clickable>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {row.do_number ?? row.id.slice(0, 8)}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
        {row.received_date ?? row.delivery_date ?? '—'}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
        {row.supplier_name ?? '—'}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
        {row.reference ?? row.supplier_invoice_number ?? '—'}
      </span>
      <InventoryStatusBadge status={statusVariant(row.status)} />
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          textAlign: 'right',
        }}
      >
        {lineRows.length}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          textAlign: 'right',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {row.invoice_total == null ? '—' : fmt(row.invoice_total)}
      </span>
      <InventoryRowActions />
    </InventoryRow>
  );
}
